import React from "react";
import { wormColors } from "core/src/core/constants.js";
import RankInsignia from "../../shared/rating/RankInsignia";
import { insigniaForValue } from "../../shared/rating/tiers";
import { flagUrlSquare, isFlagCode } from "../../shared/flags";
import s from "./ranked.module.scss";

// Match found, ported from MS Battle's MatchFound.tsx. No Accept: it plays over
// the dimmed round, holds a beat, and the countdown takes over.
//
// The seats come from the room's roster (lobby_update), the ratings from the
// server's ranked_roster message a beat after match_start: every seat gets an
// entry, null while still in placements, so no seat stands out. Until that
// message lands the badges are eggs for everyone.

export interface Seat { id: string; name: string; colorId: string; flag?: string | null }
export type SeatRatings = Record<string, { rating: number | null; games: number }> | null;

// How long the banner and the grid stay before they leave (MS Battle's timings).
export const FOUND_BANNER_MS = 3300;
export const FOUND_GRID_MS = 4900;

function Badge({ ratings, id, size }: { ratings: SeatRatings; id: string; size: number }) {
  const r = ratings ? ratings[id] : null;
  return r && r.rating != null
    ? <RankInsignia value={r.rating} size={size} />
    : <RankInsignia rating={null} placeholder size={size} placeholderTitle="Placements" />;
}

function Flag({ code, className }: { code?: string | null; className: string }) {
  return code && isFlagCode(code) ? <img className={className} src={flagUrlSquare(code)} alt="" /> : null;
}

// 1v1: your badge end and slab (your worm colour) from the left, theirs from the
// right, a black VS block between. On the phone the slabs stack and the VS is a coin.
export function FoundBanner({ me, opp, ratings, leaving }: { me: Seat; opp: Seat; ratings: SeatRatings; leaving?: boolean }) {
  return (
    <div className={`${s.stage} ${leaving ? s.leaving : ""}`} role="status" aria-label={`${me.name} versus ${opp.name}`}
      style={{ "--you": wormColors[me.colorId] || "#ff2e88", "--opp": wormColors[opp.colorId] || "#28c4d4" } as React.CSSProperties}>
      <div className={s.dimmer} />
      <div className={s.row}>
        <div className={`${s.group} ${s.groupL}`}>
          <div className={`${s.end} ${s.pg}`}><Badge ratings={ratings} id={me.id} size={36} /></div>
          <div className={`${s.slab} ${s.slabL} ${s.pg}`}><span className={s.name}>{me.name}</span><Flag code={me.flag} className={s.flag} /></div>
        </div>
        <div className={`${s.mid} ${s.pg}`}>VS</div>
        <div className={`${s.group} ${s.groupR}`}>
          <div className={`${s.slab} ${s.slabR} ${s.pg}`}><Flag code={opp.flag} className={s.flag} /><span className={s.name}>{opp.name}</span></div>
          <div className={`${s.end} ${s.pg}`}><Badge ratings={ratings} id={opp.id} size={36} /></div>
        </div>
      </div>
    </div>
  );
}

// 6 FFA: the starting grid. Six seats two by three, odd ones from the left and
// even ones from the right, the worm's colour on each seat's edge.
// 6 FFA: the starting grid, two columns by three rows over a dark band (so the
// arena's walls never sit behind a colour). A seat is a card tinted with its
// worm's colour from the left, the colour down its left edge, a disc of it,
// the name, the flag in a column of its own, and the rank badge in a lighter
// block at the end (the start page strip's way). No tier text and no highlight
// on your own seat: every card the same. The left column glides in from the
// left, the right from the right, and they leave the way they came.
// Design 1S in /debug/ffa-banner.
export function FoundGrid({ seats, youIds, ratings, leaving }: { seats: Seat[]; youIds: string[]; ratings: SeatRatings; leaving?: boolean }) {
  return (
    <div className={`${s.stage} ${leaving ? s.leaving : ""}`} role="status" aria-label="Match found">
      <div className={s.dimmer} />
      <div className={s.band} />
      <div className={s.grid6}>
        {seats.slice(0, 6).map((p, i) => (
          <div key={p.id} className={s.slot} aria-label={youIds.includes(p.id) ? `${p.name} (you)` : p.name}
            style={{ "--worm": wormColors[p.colorId] || "#28c4d4", "--from": i % 2 === 0 ? "-60%" : "60%" } as React.CSSProperties}>
            <span className={s.slotDisc} />
            <span className={s.slotName}>{p.name}</span>
            <span className={s.slotFlagCol}><Flag code={p.flag} className={s.slotFlag} /></span>
            <span className={s.slotBlock}><Badge ratings={ratings} id={p.id} size={40} /></span>
          </div>
        ))}
      </div>
    </div>
  );
}
